import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Heart, BarChart3, AlertCircle } from "lucide-react";
import Header from "@/components/Header";

const formatDate = () =>
  new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());

export default function AdminLikes() {
  const [rows, setRows] = useState([]);
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const totalLikes = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.likes || 0), 0),
    [rows]
  );

  const loadLikes = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/property-likes", {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("No se pudo leer el conteo");
      }

      const data = await response.json();
      setConfigured(Boolean(data.configured));
      setRows(Array.isArray(data.properties) ? data.properties : []);
      setUpdatedAt(formatDate());
    } catch (err) {
      setError("No se pudo cargar la contabilidad de likes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLikes();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <Header />

      <main className="mx-auto max-w-6xl px-4 pb-12">
        <div className="mb-5 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-orange-500">
              Panel interno
            </p>
            <h1 className="mt-1 text-2xl font-black text-slate-950">
              Likes por propiedad
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Conteo de corazones guardados en la base de datos.
            </p>
          </div>

          <button
            onClick={loadLikes}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0056D2] px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </button>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Total likes
              </p>
              <Heart className="h-4 w-4 text-red-500" fill="currentColor" />
            </div>
            <p className="mt-2 text-3xl font-black text-slate-950">{totalLikes}</p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Propiedades con likes
              </p>
              <BarChart3 className="h-4 w-4 text-[#0056D2]" />
            </div>
            <p className="mt-2 text-3xl font-black text-slate-950">{rows.length}</p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Ultima lectura
            </p>
            <p className="mt-3 text-sm font-semibold text-slate-800">
              {updatedAt || "Sin lectura todavia"}
            </p>
          </div>
        </div>

        {configured === false && (
          <div className="mb-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              La pantalla ya esta lista, pero la base de datos no esta conectada en
              este despliegue. Revisa las variables de Upstash en Vercel y vuelve a
              desplegar.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="text-sm font-bold text-slate-950">
              Ranking de propiedades
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm font-semibold text-slate-500">
              Cargando likes...
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm font-semibold text-slate-500">
              Todavia no hay likes guardados.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-left text-sm">
                <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Propiedad</th>
                    <th className="px-4 py-3">Ubicacion</th>
                    <th className="px-4 py-3 text-right">Likes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-950">
                          {row.title || `Propiedad ${row.id}`}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">ID {row.id}</p>
                      </td>
                      <td className="max-w-md px-4 py-3 text-slate-600">
                        {row.location || "Sin ubicacion guardada"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-sm font-black text-red-600">
                          <Heart className="h-3.5 w-3.5" fill="currentColor" />
                          {row.likes}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
